'use client';

import React from 'react';

import { BlurFade } from '../fragments/blur-fade';
import { SiteHeading } from '../fragments/site-heading';

interface TipItem {
  number: string;
  title: string;
  description: string;
  details: string;
}

interface TipsSectionProps {
  featureName: string;
  sectionTitle?: string;
  sectionDescription?: string;
  customTips?: TipItem[];
}

const TipsSection: React.FC<TipsSectionProps> = ({
  featureName,
  sectionTitle,
  sectionDescription,
  customTips
}) => {
  const tips = customTips || [
    {
      number: '01',
      title: 'Define Clear Objectives',
      description: `Give your ${featureName.toLowerCase()} video clear learning objectives and a defined purpose.`,
      details:
        'Start by outlining what viewers should learn or achieve after watching. Clear goals help structure your content and measure success.'
    },
    {
      number: '02',
      title: 'Understand Your Audience',
      description: `Adapt the material to your target audience's requirements and preferences.`,
      details:
        "Research your viewers' skill levels, interests, and pain points. Tailor your language, examples, and pacing to match their expectations."
    },
    {
      number: '03',
      title: 'Ensure Accessibility',
      description: `Use AI capabilities to improve accessibility, like auto-generated subtitles and multi-lingual support.`,
      details:
        'Include captions, transcripts, and multiple language options. This expands your reach and ensures everyone can benefit from your content.'
    },
    {
      number: '04',
      title: 'Gather Feedback',
      description: `Encourage viewers to provide feedback to refine scripts, timing, and clarity.`,
      details:
        'Use comments, surveys, and analytics to understand what works. Iterate on your content based on real viewer insights for continuous improvement.'
    }
  ];

  return (
    <section className="container bg-background">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <SiteHeading
          badge="Pro Tips"
          title={
            sectionTitle || `4 Tips for Creating Better ${featureName} Videos`
          }
          isSecondary
          description={
            sectionDescription ||
            'Follow these best practices to create more effective and engaging videos.'
          }
        />

        {/* Cards Grid - 2x2 layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6 lg:gap-8">
          {tips.map((tip, index) => (
            <BlurFade
              inView
              delay={index * 0.2}
              key={index}
              className="group bg-slate-50 dark:bg-slate-800/50 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-8 relative overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-green-500/10 dark:hover:shadow-green-500/20 border border-slate-200/60 dark:border-slate-700/50 hover:border-green-500 dark:hover:border-green-500"
            >
              {/* Number Badge */}
              <span className="inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 rounded-full bg-slate-200/80 dark:bg-slate-700 text-slate-500 dark:text-slate-400 font-semibold text-xs sm:text-sm mb-4 sm:mb-5">
                {tip.number}
              </span>

              {/* Content */}
              <div className="space-y-2 sm:space-y-3">
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-foreground group-hover:text-primary transition-colors duration-300 leading-snug">
                  {tip.title}
                </h3>
                <p className="text-xs sm:text-sm lg:text-base text-muted-foreground leading-relaxed">
                  {tip.description}
                </p>
                <p className="text-[11px] sm:text-xs lg:text-sm text-muted-foreground/70 leading-relaxed">
                  {tip.details}
                </p>
              </div>

              {/* Decorative corner accent */}
              <div className="absolute -bottom-6 -right-6 sm:-bottom-8 sm:-right-8 w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-slate-100 dark:bg-slate-700/30 group-hover:bg-slate-200 dark:group-hover:bg-slate-700/50 transition-colors duration-300" />
            </BlurFade>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TipsSection;
